import { useCallback, useEffect, useState } from 'react';

import { listPlugins, setPluginEnabled, type InstalledPlugin } from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import './AdminPages.css';

/**
 * Installed plugins.
 *
 * Name, one line, state, one toggle. Nothing else earns its place here.
 *
 * Two things the screen must not hide. A toggle takes effect at the next restart, because
 * plugins register at boot; showing it as immediate would make the screen lie for the
 * whole interval. And enabling a plugin grants it full process privileges, which is worth
 * one sentence at the top rather than a line on every row.
 */
export function AdminPlugins() {
  const [rows, setRows] = useState<InstalledPlugin[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const load = useCallback(() => {
    listPlugins()
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load plugins.'));
  }, []);

  useEffect(load, [load]);

  async function toggle(p: InstalledPlugin) {
    if (!p.id) return;
    setBusy(p.id);
    setError(null);
    try {
      await setPluginEnabled(p.id, !p.enabled);
      setPending(true);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Plugins</h1>
          <p className="adm-lede">
            Plugin code runs with full access to the engine and its secrets. There is no
            sandbox. Enable only code you would merge into this repository.
          </p>
        </div>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {pending && (
        <div className="alert alert--warn" role="status">
          Changes take effect when the engine restarts.
        </div>
      )}

      {rows == null && <p className="adm-status">Loading…</p>}
      {rows != null && rows.length === 0 && (
        <p className="adm-status adm-muted">No plugins installed.</p>
      )}

      <div className="adm-list">
        {(rows ?? []).map((p) => (
          <article className="net-row" key={p.id ?? p.folder}>
            <div className="net-row__main">
              <span className="net-row__name">{p.name ?? p.folder}</span>
              {p.description && <span className="opp-reg__hint">{p.description}</span>}
              {!p.valid && (
                <span className="adm-error">{p.error}</span>
              )}
              <div className="net-row__badges">
                <span className={`badge ${p.enabled ? 'badge--ok' : 'badge--muted'}`}>
                  {p.enabled ? 'Enabled' : 'Disabled'}
                </span>
                {p.version && <span className="badge badge--muted mono">v{p.version}</span>}
                {p.enabled && !p.active && (
                  <span className="badge badge--warn">Restart required</span>
                )}
              </div>
            </div>

            <div className="net-row__actions">
              {p.valid && p.id && (
                <button type="button" className="button button--ghost adm-btn-sm"
                  disabled={busy === p.id} onClick={() => void toggle(p)}>
                  {p.enabled ? 'Disable' : 'Enable'}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
